import { useCallback } from 'react'

import type { SortDescriptor } from '@heroui/react'
import { cn, Table } from '@heroui/react'
import { useTranslation } from 'react-i18next'

import { Pagination } from '@/components/ui/Pagination'
import { RowsPerPage } from '@/components/ui/RowsPerPage'
import { useUsersStore } from '@/store/users.store'

import { UserTableRow } from './UserTableRow'

interface UsersTableProps {
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  sortDescriptor: SortDescriptor;
  onSortChange: (descriptor: SortDescriptor) => void;
}

function SortableColumnHeader({
  children,
  sortDirection,
}: {
  children: React.ReactNode;
  sortDirection?: 'ascending' | 'descending';
}) {
  return (
    <span className="flex items-center justify-between gap-1">
      {children}
      {!!sortDirection && (
        <svg
          width="12" height="12" viewBox="0 0 24 24" fill="none"
          className={cn(
            'transform transition-transform duration-150 ease-out',
            sortDirection === 'descending' ? 'rotate-180' : '',
          )}
        >
          <path d="M7 14l5-5 5 5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"
                strokeLinejoin="round"
          />
        </svg>
      )}
    </span>
  )
}

export function UsersTable({
  onEdit,
  onDelete,
  sortDescriptor,
  onSortChange,
}: UsersTableProps) {
  const { t } = useTranslation()
  const { users, pagination, setPagination } = useUsersStore()

  const handlePageChange = useCallback((pageNumber: number) => {
    setPagination({ page: pageNumber })
  }, [setPagination])

  const handleLimitChange = useCallback((limit: number) => {
    setPagination({ limit, page: 0 })
  }, [setPagination])

  const start = (pagination.page === 0 ? 0 : pagination.page) * pagination.limit + 1
  const end = Math.min((pagination.page + 1) * pagination.limit, pagination.total)

  return (
    <Table>
      <Table.ScrollContainer className="max-h-[calc(100vh-theme(spacing.48))]">
        <Table.Content
          aria-label="Users list table"
          sortDescriptor={sortDescriptor}
          onSortChange={onSortChange}
        >
          <Table.Header className="sticky top-0 z-10">
            <Table.Column allowsSorting id="name">
              {({ sortDirection }) => (
                <SortableColumnHeader
                  sortDirection={sortDirection}
                >{t('users.full_name')}</SortableColumnHeader>
              )}
            </Table.Column>
            <Table.Column allowsSorting id="email">
              {({ sortDirection }) => (
                <SortableColumnHeader
                  sortDirection={sortDirection}
                >{t('users.email')}</SortableColumnHeader>
              )}
            </Table.Column>
            <Table.Column allowsSorting id="role">
              {({ sortDirection }) => (
                <SortableColumnHeader
                  sortDirection={sortDirection}
                >{t('users.role')}</SortableColumnHeader>
              )}
            </Table.Column>
            <Table.Column id="verified">
              {t('users.verified')}
            </Table.Column>

            <Table.Column></Table.Column>
          </Table.Header>
          <Table.Body>
            {users.map(u => (
              <UserTableRow
                key={u.id}
                user={u}
                onEdit={onEdit}
                onDelete={onDelete}
              />
            ))}
          </Table.Body>
        </Table.Content>
      </Table.ScrollContainer>
      <Table.Footer>
        <div className="flex w-full items-center justify-between">
          <div className="flex items-center gap-4">
            <RowsPerPage
              value={pagination.limit}
              onChange={handleLimitChange}
            />
            <span className="text-xs text-foreground/50">
              {t('common.pagination_summary', { start, end, total: pagination.total })}
            </span>
          </div>
          <Pagination
            page={pagination.page}
            total={pagination.totalPages}
            onChange={handlePageChange}
          />
        </div>
      </Table.Footer>
    </Table>
  )
}
